import { ManagedAgentResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { AgentDetailRow } from '@/components/agents/AgentDetailRow';
import { AgentAvatar } from '@/components/chat/MessageComposer/AgentAvatar';
import { AgentCodeWriteBadge } from '@/components/chat/MessageComposer/AgentBadges/AgentCodeWriteBadge';
import { AgentInternetAccessBadge } from '@/components/chat/MessageComposer/AgentBadges/AgentInternetAccessBadge';
import { AgentRlsBadge } from '@/components/chat/MessageComposer/AgentBadges/AgentRlsBadge';
import { AgentSourcesBadges } from '@/components/chat/MessageComposer/AgentBadges/AgentSourcesBadges';
import { Button } from '@/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '@/components/ui/collapsible';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { Skeleton } from '@/components/ui/skeleton';

import { useAgentInformation } from '@/hooks/useAgentInformation';
import { useAgentRoster } from '@/hooks/useAgentRoster';

export function AgentsPanel() {
    const { agents, selected, loading } = useAgentRoster();

    if (loading) {
        return (
            <div className="flex flex-col gap-3">
                {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="rounded-lg w-full h-36" />
                ))}
            </div>
        );
    }

    if (agents.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center gap-y-1 py-8 text-center">
                <p className="text-secondary-foreground text-base">
                    No agents available
                </p>
                <p className="text-muted-foreground text-xs">
                    Agents registered for your organization will appear here
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {agents.map((agent) => (
                <AgentRow
                    key={agent.guid}
                    agent={agent}
                    active={selected.includes(agent.agentId)}
                />
            ))}
        </div>
    );
}

interface AgentRowProps {
    agent: ManagedAgentResponseModel;
    active: boolean;
}

function AgentRow({ agent, active }: AgentRowProps) {
    const [open, setOpen] = useState(true);
    const {
        loading,
        mcpServerNames,
        description,
        hasInternetAccess,
        hasCodeWriteAccess
    } = useAgentInformation(agent, { lazy: false });

    const hasTools =
        hasInternetAccess || hasCodeWriteAccess || !!agent.enableRls;

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            data-agent-id={agent.agentId}
            className="flex flex-col bg-background px-3.5 py-2 border border-border rounded-lg transition-colors"
        >
            <CollapsibleTrigger asChild>
                <div
                    role="button"
                    tabIndex={0}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${
                        agent.displayName
                    }`}
                    className="flex items-center gap-3 py-2 rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer"
                >
                    <AgentAvatar
                        agentId={agent.agentId}
                        className="size-8"
                        animate={active}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                            {agent.displayName}
                        </div>
                        <div className="text-muted-foreground text-xs">
                            {active ? 'Active in this chat' : 'Not active'}
                        </div>
                    </div>
                    <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        tabIndex={-1}
                        className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                        <span>
                            <ChevronDown
                                className={cn(
                                    'transition-transform duration-200',
                                    open && 'rotate-180'
                                )}
                            />
                        </span>
                    </Button>
                </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
                <AgentDetailRow label="About">
                    {loading ? (
                        <div className="flex flex-col gap-1.5 py-1">
                            <Skeleton className="w-full h-3" />
                            <Skeleton className="w-4/5 h-3" />
                        </div>
                    ) : (
                        <MarkdownContent className="font-light text-secondary-foreground text-sm">
                            {description}
                        </MarkdownContent>
                    )}
                </AgentDetailRow>

                <AgentDetailRow label="Tools" alignToBadges>
                    {loading ? (
                        <Skeleton className="rounded-full w-28 h-7" />
                    ) : hasTools ? (
                        <div className="flex flex-wrap gap-1.5">
                            <AgentInternetAccessBadge
                                hasInternetAccess={hasInternetAccess}
                            />
                            <AgentCodeWriteBadge
                                hasCodeWriteAccess={hasCodeWriteAccess}
                            />
                            <AgentRlsBadge hasRls={!!agent.enableRls} />
                        </div>
                    ) : (
                        <span className="text-muted-foreground text-xs leading-7">
                            None
                        </span>
                    )}
                </AgentDetailRow>

                <AgentDetailRow label="Sources" alignToBadges>
                    <div className="flex items-center min-h-7">
                        <AgentSourcesBadges
                            loading={loading}
                            mcpServerNames={mcpServerNames}
                        />
                    </div>
                </AgentDetailRow>
            </CollapsibleContent>
        </Collapsible>
    );
}
